import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { BrandedString } from 'src/helpers/brandedString';
import { useEvaluations } from 'src/hooks/api';
import { EvaluationInfo, Resource } from 'src/types/rest/managementPortalApi';

interface UseEvaluationsProps<TAction extends BrandedString> {
  actions: TAction[];
  requiredActions?: TAction[];
  resource: Required<Resource>;
}

export const useInstanceEvaluations = <TAction extends BrandedString>({
  actions,
  requiredActions,
  resource,
}: UseEvaluationsProps<TAction>) => {
  const { isAuthenticated, user } = useAuth();

  /**
   * Memoize evaluations to maintain a stable reference across renders.
   *
   * Without this, a new array is created every render, which can cause
   * unnecessary re-fetches if useEvaluations uses it as part of a query key.
   * We derive primitive deps from `actions` and `resource` to avoid relying
   * on the caller to memoize them.
   */
  const evaluations: EvaluationInfo[] = useMemo(
    () => actions.map((action) => ({ action: { name: action }, resource })),
    [actions.join(','), resource.id, resource.type],
  );

  // Only fetch evaluations if user is authenticated
  const enabled = isAuthenticated;

  const { data, isError, isPending } = useEvaluations({
    evaluations,
    options: {
      enabled,
    },
    subject: {
      id: user?.profile.sub,
      type: 'user',
    },
  });

  const can = useCallback(
    (action: TAction) => {
      if (!data?.evaluations) return false;

      return data.evaluations.some(
        ({ context, decision }) =>
          decision &&
          context?.resource.id === resource.id &&
          context?.resource.type === resource.type &&
          context?.action.name === action,
      );
    },
    [data?.evaluations, resource.id, resource.type],
  );

  /**
   * Throw 403 if any required action is denied once evaluations have loaded.
   *
   * - Skipped while loading or on error — the caller handles those states.
   * - Throwing here is intentional: the `RootBoundary` catches it just as it
   *   would a throw from a component.
   */
  if (
    !isPending &&
    !isError &&
    requiredActions?.some((action) => !can(action))
  ) {
    throw new Response('Forbidden', { status: 403 });
  }

  return {
    can,
    evaluations: data?.evaluations,
    isError,
    isPending,
  };
};
